import { css } from '@emotion/react'
import { Icon, IconImage, Theme, useTheme } from 'bold-ui'

interface MapMarkerProps {
  icon: IconImage
  color?: string
  label?: string
  onClick?: () => void
}

const PINO_WIDTH = 32
const PINO_HEIGHT = 40

export function MapMarker(props: Readonly<MapMarkerProps>) {
  const { icon, color, label, onClick } = props

  const theme = useTheme()
  const styles = createStyles(theme, color, !!onClick)

  const content = (
    <>
      <svg css={styles.shape} viewBox={`0 0 ${PINO_WIDTH} ${PINO_HEIGHT}`} xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M16 40C5.334 29.892 0 21.892 0 16C0 11.7565 1.68571 7.68687 4.68629 4.68629C7.68687 1.68571 11.7565 0 16 0C20.2435 0 24.3131 1.68571 27.3137 4.68629C30.3143 7.68687 32 11.7565 32 16C32 21.892 26.666 29.892 16 40ZM16 22C17.5913 22 19.1174 21.3679 20.2426 20.2426C21.3679 19.1174 22 17.5913 22 16C22 14.4087 21.3679 12.8826 20.2426 11.7574C19.1174 10.6321 17.5913 10 16 10C14.4087 10 12.8826 10.6321 11.7574 11.7574C10.6321 12.8826 10 14.4087 10 16C10 17.5913 10.6321 19.1174 11.7574 20.2426C12.8826 21.3679 14.4087 22 16 22Z'
          fill={theme.pallete.surface.main}
        />
        <path
          d='M16 22C17.5913 22 19.1174 21.3679 20.2426 20.2426C21.3679 19.1174 22 17.5913 22 16C22 14.4087 21.3679 12.8826 20.2426 11.7574C19.1174 10.6321 17.5913 10 16 10C14.4087 10 12.8826 10.6321 11.7574 11.7574C10.6321 12.8826 10 14.4087 10 16C10 17.5913 10.6321 19.1174 11.7574 20.2426C12.8826 21.3679 14.4087 22 16 22Z'
          fill={theme.pallete.surface.main}
        />
      </svg>
      <div css={styles.circle}>
        <Icon icon={icon} size={1} style={styles.icon} />
      </div>
    </>
  )

  if (onClick) {
    return (
      <button type='button' css={styles.root} onClick={onClick} aria-label={label}>
        {content}
      </button>
    )
  }

  return <div css={styles.root}>{content}</div>
}

const createStyles = (theme: Theme, color: string | undefined, isClickable: boolean) => ({
  root: css`
    position: relative;
    display: inline-block;
    width: ${PINO_WIDTH}px;
    height: ${PINO_HEIGHT}px;
    filter: drop-shadow(0px 1.33px 6.67px rgba(0, 0, 0, 0.12)) drop-shadow(0px 2.67px 1.33px rgba(0, 0, 0, 0.04));
    cursor: ${isClickable ? 'pointer' : 'inherit'};
    border: none;
    margin: 0;
    padding: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: inherit;
  `,
  shape: css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  `,
  circle: css`
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${color ?? theme.pallete.primary.c60};
    z-index: ${theme.zIndex.popper};
  `,
  icon: css`
    fill: ${theme.pallete.surface.main};
  `,
})
